<script setup>
import { normalizeTestimonials } from '~/composables/normalizeTestimonials'

const { model, variation } = defineProps({
  model: {
    type: Object,
  },
  variation: {
    type: String,
    default: 'four-col',
  },
})

const isTwoCol = computed(() => variation === 'two-col')

const normalizedTestimonials = computed(() =>
  normalizeTestimonials(model?.testimonials),
)

// Trim so a blank/whitespace-only CMS name renders no <h3> rather than an empty one.
function feedName(testimonial) {
  return `${testimonial?.testimonialName?.value || testimonial?.authorName?.value || ''}`.trim()
}
function stackedName(testimonial) {
  return `${testimonial?.authorName?.value || ''}`.trim()
}
</script>

<template>
  <div
    class="testimonial-listing-block block-spacing"
    :class="`testimonial-listing-block--${variation}`"
  >
    <div class="container-fluid">
      <div class="testimonial-listing-block__header 3xl:max-w-block-wide mx-auto">
        <div
          v-if="model?.title?.value"
          class="prose mb-5 lg:mb-7.5"
        >
          <h2>{{ model.title.value }}</h2>
        </div>
        <div
          v-if="model?.body?.value"
          class="testimonial-listing-block__body prose text-med-navy mb-5 lg:mb-7.5"
          v-html="model?.body?.value"
        />
      </div>
      <div class="testimonial-listing-block__grid flex 3xl:max-w-block-wide mr-auto ml-auto">
        <!-- Feed variation: image-left card linking to the testimonial page -->
        <template v-if="isTwoCol">
          <div
            v-for="testimonial in normalizedTestimonials"
            :key="testimonial.contentLink.id"
            class="testimonial-listing-block__card prose"
          >
            <div class="testimonial-listing-block__image-wrap">
              <div class="testimonial-listing-block__image-container w-[240px]">
                <ULink
                  v-if="testimonial.aprimoMainImage?.url && testimonial.pageUrl?.value"
                  :to="useCmsUrl(testimonial.pageUrl.value)"
                  :locale="false"
                  :aria-label="feedName(testimonial)"
                >
                  <NuxtImg
                    provider="fastlyAprimo"
                    :src="testimonial.aprimoMainImage.url"
                    class="testimonial-listing-block__image rounded-xl not-prose"
                    :alt="testimonial.aprimoMainImageAltText?.value || ''"
                    :width="240"
                    :height="160"
                    fit="bounds"
                    sizes="240px"
                    loading="lazy"
                  />
                </ULink>
              </div>
            </div>
            <div class="testimonial-listing-block__content">
              <h3
                v-if="feedName(testimonial)"
                class="h4 testimonial-listing-block__name"
              >
                <ULink
                  v-if="testimonial.pageUrl?.value"
                  :to="useCmsUrl(testimonial.pageUrl.value)"
                  :locale="false"
                >
                  {{ feedName(testimonial) }}
                </ULink>
                <template v-else>
                  {{ feedName(testimonial) }}
                </template>
              </h3>
              <p
                v-if="testimonial.description?.value"
                class="testimonial-listing-block__summary"
              >
                {{ testimonial.description.value }}
              </p>
            </div>
          </div>
        </template>
        <!-- Default variation: stacked card (image above name, job title, quote) -->
        <template v-else>
          <div
            v-for="testimonial in normalizedTestimonials"
            :key="testimonial.contentLink.id"
            class="testimonial-listing-block__card flex prose"
          >
            <div class="testimonial-listing-block__image-wrap">
              <div class="testimonial-listing-block__image-container w-[240px] mx-auto">
                <NuxtImg
                  v-if="testimonial.aprimoMainImage?.url"
                  provider="fastlyAprimo"
                  :src="testimonial.aprimoMainImage.url"
                  class="testimonial-listing-block__image rounded-xl not-prose"
                  :alt="testimonial.aprimoMainImageAltText?.value || ''"
                  :width="240"
                  :height="160"
                  fit="bounds"
                  sizes="240px"
                  loading="lazy"
                />
              </div>
            </div>
            <div
              v-if="stackedName(testimonial) || testimonial.quote?.value"
              class="testimonial-listing-block__content"
            >
              <h3
                v-if="stackedName(testimonial)"
                class="h4 testimonial-listing-block__name"
              >
                <ULink
                  v-if="testimonial.pageUrl?.value"
                  :to="useCmsUrl(testimonial.pageUrl.value)"
                  :locale="false"
                >
                  {{ stackedName(testimonial) }}
                </ULink>
                <template v-else>
                  {{ stackedName(testimonial) }}
                </template>
              </h3>
              <p
                v-if="testimonial.description?.value"
                class="italic"
              >
                {{ testimonial.description.value }}
              </p>
              <p
                v-if="testimonial.quote?.value"
                class="testimonial-listing-block__quote"
              >
                {{ testimonial.quote.value }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div>
      <slot name="pagination" />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/blocks/testimonial-listing-block.scss';
</style>
